import { useState } from 'react'
import { FileCode, Eye, Code, Copy, Check, Download, Bot, Layout, FileText, PartyPopper, Rocket, Sparkles, DollarSign, ExternalLink } from 'lucide-react'
import { useStore } from '@store/useStore'
import PipelineProgress from './PipelineProgress'
import AgentSummaryStack from './AgentSummaryStack'

export default function PagesPanel() {
  const { files, selectedFile, selectFile, ui, setPreviewMode, project, summaries, linkPlacements } = useStore()
  const [copied, setCopied] = useState(null)
  const [selectedBlueprintPage, setSelectedBlueprintPage] = useState(null)

  const fileList = Object.keys(files)
  const isGenerating = ui.isLoading
  const hasPipeline = project.domain || isGenerating
  const hasContext = Object.keys(project.context || {}).length > 0
  const currentContent = selectedFile ? files[selectedFile] : null
  const summaryCount = Object.keys(summaries || {}).length

  // Get blueprint pages - architecte agent now exposes at top level
  const arch = project.context?.architecte
  const blueprintPages = arch?.pages || arch?.architecture?.pages || []
  const hasBlueprint = blueprintPages.length > 0

  // Helper: check if page has money site link
  const hasMoneySiteLink = (filename) => {
    return linkPlacements?.moneySite?.page === filename
  }

  // Helper: check if page has resource links
  const hasResourceLink = (filename) => {
    return linkPlacements?.resourceLinks?.some(rl => rl.pages?.includes(filename))
  }

  // Get tier for a page from blueprint
  const getPageTier = (filename) => {
    const page = blueprintPages.find(p => p.file === filename)
    return page?.tier || null
  }

  const handleCopy = (filename) => {
    navigator.clipboard.writeText(files[filename])
    setCopied(filename)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleDownload = (filename) => {
    const blob = new Blob([files[filename]], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  // Panel logic:
  // - User can switch tabs manually anytime
  // - During generation WITH files: prefer pages view to show progress
  // - Otherwise: respect user's choice or default to pipeline if active
  const activePanel = ui.rightPanel === 'pages'
    ? 'pages'  // Always respect user's explicit choice
    : (fileList.length > 0 && isGenerating)
      ? 'pages'  // Auto-switch to pages when files start appearing during generation
      : (isGenerating || hasPipeline)
        ? 'pipeline'
        : 'pages'

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b border-slate-800">
        {hasPipeline && (
          <button
            onClick={() => useStore.getState().setRightPanel('pipeline')}
            className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
              activePanel === 'pipeline'
                ? 'text-white border-b-2 border-primary-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Bot className="w-4 h-4 inline mr-1.5" />
            Agents IA
            {isGenerating && (
              <span className="ml-1.5 w-2 h-2 rounded-full bg-primary-500 animate-pulse inline-block" />
            )}
            {summaryCount > 0 && !isGenerating && (
              <span className="ml-1.5 text-xs text-accent-400">({summaryCount})</span>
            )}
          </button>
        )}
        <button
          onClick={() => useStore.getState().setRightPanel('pages')}
          className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
            activePanel === 'pages'
              ? 'text-white border-b-2 border-primary-500'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <FileCode className="w-4 h-4 inline mr-1.5" />
          Pages ({fileList.length})
        </button>
      </div>

      {/* Pipeline view - live progress + stacked summaries */}
      {activePanel === 'pipeline' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Live progress at top - compact, doesn't grow */}
          <div className="flex-shrink-0">
            <PipelineProgress />
          </div>

          {/* Stacked summaries below - AgentSummaryStack handles its own scrolling */}
          <div className="border-t border-slate-800 flex-1 overflow-hidden">
            <AgentSummaryStack />
          </div>
        </div>
      )}

      {activePanel === 'pages' && (
        <>
          {/* File list OR Blueprint pages */}
          <div className="border-b border-slate-800">
            <div className="p-2 max-h-48 overflow-y-auto">
              {fileList.length === 0 && !hasBlueprint ? (
                <p className="text-slate-500 text-sm p-2">
                  Aucune page définie. Lance la préparation avec un domaine.
                </p>
              ) : fileList.length === 0 && hasBlueprint ? (
                // Show blueprint pages (before generation)
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 px-2 py-1 flex items-center gap-1">
                    <Layout className="w-3 h-3" />
                    Blueprint ({blueprintPages.length} pages)
                  </p>
                  {blueprintPages.map((page, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedBlueprintPage(page)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        selectedBlueprintPage?.slug === page.slug
                          ? 'bg-violet-600/20 text-violet-400'
                          : 'hover:bg-slate-800 text-slate-300'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="truncate">{page.title || page.slug}</span>
                      </span>
                      <span className="text-xs text-slate-500">{page.tier || 'T1'}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {fileList.map((filename) => {
                    const tier = getPageTier(filename)
                    const hasMoney = hasMoneySiteLink(filename)
                    const hasResource = hasResourceLink(filename)

                    return (
                      <button
                        key={filename}
                        onClick={() => selectFile(filename)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                          selectedFile === filename
                            ? 'bg-primary-600/20 text-primary-400'
                            : 'hover:bg-slate-800 text-slate-300'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <FileCode className="w-4 h-4" />
                          <span className="truncate">{filename}</span>
                          {/* Link indicators */}
                          {hasMoney && (
                            <span title="Money site link" className="flex items-center">
                              <DollarSign className="w-3.5 h-3.5 text-amber-400" />
                            </span>
                          )}
                          {hasResource && (
                            <span title="Resource link (webographie)" className="flex items-center">
                              <ExternalLink className="w-3.5 h-3.5 text-blue-400" />
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-1">
                          {/* Tier badge */}
                          {tier && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              tier === 'T1' ? 'bg-violet-500/20 text-violet-400' :
                              tier === 'T2' ? 'bg-blue-500/20 text-blue-400' :
                              tier === 'T3' ? 'bg-slate-500/20 text-slate-400' :
                              'bg-slate-600/20 text-slate-500'
                            }`}>
                              {tier}
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCopy(filename)
                            }}
                            className="p-1 hover:bg-slate-700 rounded"
                          >
                            {copied === filename ? (
                              <Check className="w-3 h-3 text-green-400" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDownload(filename)
                            }}
                            className="p-1 hover:bg-slate-700 rounded"
                          >
                            <Download className="w-3 h-3" />
                          </button>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Preview/Code toggle */}
          {selectedFile && (
            <div className="flex border-b border-slate-800">
              <button
                onClick={() => setPreviewMode('code')}
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  ui.previewMode === 'code'
                    ? 'text-white bg-slate-800'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Code className="w-3 h-3 inline mr-1" />
                Code
              </button>
              <button
                onClick={() => setPreviewMode('preview')}
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  ui.previewMode === 'preview'
                    ? 'text-white bg-slate-800'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Eye className="w-3 h-3 inline mr-1" />
                Preview
              </button>
            </div>
          )}

          {/* Content area */}
          <div className="flex-1 overflow-hidden">
            {/* Blueprint page details (before generation) */}
            {!selectedFile && selectedBlueprintPage ? (
              <div className="h-full overflow-auto p-4">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{selectedBlueprintPage.title}</h3>
                    <p className="text-sm text-slate-400">{selectedBlueprintPage.slug}.html</p>
                  </div>
                  {selectedBlueprintPage.description && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-1">Description</p>
                      <p className="text-sm text-slate-300">{selectedBlueprintPage.description}</p>
                    </div>
                  )}
                  {selectedBlueprintPage.keywords && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-1">Mots-clés</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedBlueprintPage.keywords.map((kw, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-800 rounded text-xs text-slate-300">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedBlueprintPage.sections && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-1">Sections</p>
                      <ul className="space-y-1">
                        {selectedBlueprintPage.sections.map((section, i) => (
                          <li key={i} className="text-sm text-slate-300 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500"></span>
                            {section.title || section}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="pt-4 border-t border-slate-800">
                    <p className="text-xs text-slate-500">
                      Clique sur "Générer le site" pour créer le HTML de cette page.
                    </p>
                  </div>
                </div>
              </div>
            ) : !selectedFile ? (
              <div className="h-full flex items-center justify-center text-slate-500">
                Sélectionne une page
              </div>
            ) : ui.previewMode === 'code' ? (
              <pre className="h-full overflow-auto p-4 text-xs bg-slate-900">
                <code className="text-slate-300">{currentContent}</code>
              </pre>
            ) : (
              <iframe
                srcDoc={currentContent}
                className="w-full h-full bg-white"
                title={selectedFile}
              />
            )}
          </div>
        </>
      )}

    </div>
  )
}
